'use client'

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode
} from 'react'
import { getUserPlan } from '@/lib/strip-service.ts'
import { getUserData } from '@/app/(main)/agency/(auth)/helper'
import { usePathname } from 'next/navigation'

type SubscriptionPlan = string

interface SubscriptionContextType {
  plan: SubscriptionPlan
  isLoading: boolean
  error: string | null
  isRedirected: boolean
  setIsRedirected: (value: boolean) => void
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(
  undefined
)

const UNAUTHENTICATED_ROUTES = [
  '/agency/signin',
  '/agency/signup',
  '/agency/signup?step=otp',
  '/agency/signup?step=name',
  '/agency/signup?step=company',
  '/agency/password-recovery',
  '/agency/reset-password'
]

interface Props {
  children: ReactNode
}

// Dummy API
const checkTestMode = async (): Promise<number> => {
  await new Promise(res => setTimeout(res, 500))
  return Math.random() > 0.5 ? 1 : 0
}

const shouldSkipSubscriptionCheck = (pathname: string): boolean => {
  return UNAUTHENTICATED_ROUTES.some(
    route => pathname === route || pathname.startsWith(`${route}/`)
  )
}

export const SubscriptionProvider = ({ children }: Props) => {
  const [plan, setPlan] = useState<SubscriptionPlan>('')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isRedirected, setIsRedirected] = useState(false)
  const pathname = usePathname()

  useEffect(() => {
    if (shouldSkipSubscriptionCheck(pathname)) {
      setIsLoading(false)
      setPlan('')
      setError(null)
      return
    }
    const fetchPlan = async () => {
      const currentUser = getUserData()
      // console.log('----------------------\ncurrentUser\n', currentUser);
      if (!currentUser?.userId) {
        setError('User not found')
        // setPlan('');
        // setTestmode(await checkTestMode());
        setIsLoading(false)
        return
      }
      setIsLoading(true)
      try {
        const response = await getUserPlan(currentUser.userId)
        // console.log('----------------------\nresponse data\n', response.data);
        let currentPlan: string = ''

        if (response?.data && 'plan' in response.data) {
          currentPlan = response.data.plan
          //   console.log('----------------------\nPlan\n', currentPlan);
          setPlan(currentPlan)
          setError(null)
        } else {
          setPlan('')
          setError(null)
        }
        setIsRedirected(false)

      } catch (err: any) {
        setError(err.message || 'Failed to fetch subscription plan')
        setPlan('')
      } finally {
        setIsLoading(false)
      }
    }

    fetchPlan()
  }, [pathname])

  return (
    <SubscriptionContext.Provider value={{ plan, isLoading, isRedirected, error, setIsRedirected }}>
      {children}
    </SubscriptionContext.Provider>
  )
}

export function useSubscription(): SubscriptionContextType {
  const context = useContext(SubscriptionContext)
  if (!context) {
    throw new Error(
      'useSubscription must be used within a SubscriptionProvider'
    )
  }
  return context
}
